import { FC } from "react";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import { FaUserEdit, FaUserMinus } from "react-icons/fa";
import { mapNodes } from "app/util/graph";
import { CompanyUserListFieldsFragment, useCompanyUsersQuery } from "app/api/graph/types";
import { companyGraphId } from "app/api/graph/helpers";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";
import { editCompanyUserRoute, removeCompanyUserRoute } from "app/route/Routes";
import { companyRoleLabel } from "app/api/graph/strings";
import { BlockButtonGroup } from "app/component/button/BlockButtonGroup";
import { ButtonLink } from "app/component/button/ButtonLink";
import { useCompanyIdPermissions } from "app/api/graph/hook/use-company-permissions.ts";
import { useGeneralPermissions } from "app/api/graph/hook/use-general-permissions";

type TableRowProps = {
    companyId: number;
    companyUser: CompanyUserListFieldsFragment;
    canRemove: boolean;
};

const TableRow = ({ companyId, companyUser, canRemove }: TableRowProps) => {
    const permissions = useGeneralPermissions(companyUser.permissions);

    return (
        <tr key={companyUser.id}>
            <td>{companyUser.user.fullName}</td>
            <td>{companyRoleLabel(companyUser.role)}</td>
            <td>
                {permissions.canUpdate && (
                    <ButtonLink
                        variant="warning"
                        to={editCompanyUserRoute.build({ companyId, companyUserId: companyUser._id })}
                        size="sm"
                        title="Update"
                    >
                        <FaUserEdit />
                    </ButtonLink>
                )}
                {canRemove && (
                    <ButtonLink
                        variant="danger"
                        to={removeCompanyUserRoute.build({ companyId, userId: companyUser.user._id })}
                        size="sm"
                        title="Remove"
                    >
                        <FaUserMinus />
                    </ButtonLink>
                )}
            </td>
        </tr>
    );
};

type Props = {
    companyId: number;
};

export const CompanyUsersTable: FC<Props> = ({ companyId }) => {
    const { data, loading, error, fetchMore } = useCompanyUsersQuery({
        variables: { companyId: companyGraphId(companyId) },
        notifyOnNetworkStatusChange: true,
    });
    const companyUsers = data?.company?.users;
    const { canRemoveUser } = useCompanyIdPermissions(companyGraphId(companyId));

    if (loading && !companyUsers) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!companyUsers) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    const fetchMoreUsers = () => fetchMore({ variables: { after: companyUsers.pageInfo.endCursor } });

    return (
        <Table responsive="sm" bordered className="crud-table">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {mapNodes(companyUsers, (companyUser) => (
                    <TableRow
                        key={companyUser.id}
                        companyUser={companyUser}
                        companyId={companyId}
                        canRemove={canRemoveUser}
                    />
                ))}
                {companyUsers.edges?.length === 0 ? (
                    <tr>
                        <td colSpan={100}>No users.</td>
                    </tr>
                ) : null}
                {loading ? (
                    <tr>
                        <td colSpan={100}>
                            <LoadingSpinner />
                        </td>
                    </tr>
                ) : null}
                {companyUsers.pageInfo.hasNextPage ? (
                    <tr>
                        <td colSpan={100}>
                            <BlockButtonGroup>
                                <Button variant="light" onClick={fetchMoreUsers}>
                                    Load more
                                </Button>
                            </BlockButtonGroup>
                        </td>
                    </tr>
                ) : null}
            </tbody>
        </Table>
    );
};
